import type { WorldLayout } from '../game/worldLayout';
import type {
  AnnotationDoorTarget,
  AnnotationInteraction,
  AnnotationInteractionEmoteEffect,
  AnnotationTrigger,
  AnnotationTriggerEffect,
  AnnotationTriggerEvent,
  WorldAnnotation,
} from '../types/worldAnnotation';
import {
  getAllowedTriggerEffectTypes,
  normalizeAnnotationStateRecord,
} from './worldAnnotationRuntime';
import {
  buildLegacyConditionGroup,
  normalizeAnnotationConditionGroup,
} from './worldAnnotationConditions';
import {
  DEFAULT_INTERACTION_RADIUS,
  clampRectToLayout,
  clampTile,
  normalizeCapacity,
  normalizeCategory,
  normalizeDoorTarget,
  normalizeFunction,
  normalizePosition,
  normalizeProductionConfig,
  normalizeResourceNodeConfig,
  normalizeShape,
  normalizeSize,
  preserveString,
  sanitizeString,
  toFiniteNumber,
} from './worldAnnotationNormalizers';

const clampDraftNavigatePayload = (
  value: unknown,
  layout: WorldLayout,
  annotation: WorldAnnotation,
): AnnotationDoorTarget => {
  const fallbackZone =
    annotation.doorTarget && typeof annotation.doorTarget.zone === 'string'
      ? annotation.doorTarget.zone
      : 'default';
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {
      tileX: annotation.position.x,
      tileY: annotation.position.y,
      zone: fallbackZone,
    };
  }

  const entry = value as Record<string, unknown>;
  return {
    tileX: clampTile(entry.tileX, layout.tileWidth - 1),
    tileY: clampTile(entry.tileY, layout.tileHeight - 1),
    zone: preserveString(entry.zone, fallbackZone),
  };
};

const clampDraftEmotePayload = (value: unknown): AnnotationInteractionEmoteEffect => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { emote: 'thinking' };
  }

  const entry = value as Record<string, unknown>;
  const duration = Number(entry.duration);
  return {
    emote: preserveString(entry.emote, 'thinking'),
    ...(Number.isFinite(duration)
      ? { duration: Math.max(1, Math.floor(toFiniteNumber(entry.duration, 1))) }
      : {}),
  };
};

const normalizeDraftTriggerEvent = (value: unknown): AnnotationTriggerEvent =>
  value === 'proximity_exit' ||
  value === 'interact' ||
  value === 'state_change' ||
  value === 'event'
    ? value
    : 'proximity_enter';

const normalizeDraftTriggerEffectType = (
  event: AnnotationTriggerEvent,
  value: unknown,
): AnnotationTriggerEffect['type'] => {
  const allowed = getAllowedTriggerEffectTypes(event);
  if (
    value === 'animation' ||
    value === 'sound' ||
    value === 'emote' ||
    value === 'event' ||
    value === 'state_change'
  ) {
    return allowed.includes(value) ? value : (allowed[0] ?? 'animation');
  }
  return allowed[0] ?? 'animation';
};

const clampDraftTriggerEffect = (
  event: AnnotationTriggerEvent,
  value: unknown,
): AnnotationTriggerEffect => {
  const entry =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const type = normalizeDraftTriggerEffectType(event, entry.type);
  const payload =
    entry.payload && typeof entry.payload === 'object' && !Array.isArray(entry.payload)
      ? (entry.payload as Record<string, unknown>)
      : {};

  if (type === 'animation') {
    return {
      type,
      payload: {
        variant: payload.variant === 'highlight' ? 'highlight' : 'pulse',
        ...(preserveString(payload.color) ? { color: preserveString(payload.color) } : {}),
        durationMs: Math.max(100, Math.floor(toFiniteNumber(payload.durationMs, 1_200))),
        ...(typeof payload.loop === 'boolean' ? { loop: payload.loop } : {}),
      },
    };
  }

  if (type === 'sound') {
    return {
      type,
      payload: {
        kind: payload.kind === 'noise' ? 'noise' : 'tone',
        waveform:
          payload.waveform === 'triangle' ||
          payload.waveform === 'square' ||
          payload.waveform === 'sawtooth'
            ? payload.waveform
            : 'sine',
        frequency: Math.max(40, Math.floor(toFiniteNumber(payload.frequency, 440))),
        gain: Math.max(0, Math.min(1, toFiniteNumber(payload.gain, 0.05))),
        durationMs: Math.max(100, Math.floor(toFiniteNumber(payload.durationMs, 1_200))),
        ...(typeof payload.loop === 'boolean' ? { loop: payload.loop } : {}),
      },
    };
  }

  if (type === 'emote') {
    return {
      type,
      payload: {
        emote: preserveString(payload.emote, 'thinking'),
        durationMs: Math.max(100, Math.floor(toFiniteNumber(payload.durationMs, 1_500))),
        ...(typeof payload.loop === 'boolean' ? { loop: payload.loop } : {}),
      },
    };
  }

  if (type === 'event') {
    return {
      type,
      payload: {
        name: preserveString(payload.name, 'annotation.trigger'),
        ...(normalizeAnnotationStateRecord(payload.detail)
          ? { detail: normalizeAnnotationStateRecord(payload.detail) }
          : {}),
      },
    };
  }

  return {
    type,
    payload: {
      state: normalizeAnnotationStateRecord(payload.state) ?? { active: true },
    },
  };
};

const clampDraftAnnotationTriggers = (value: unknown): AnnotationTrigger[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const triggers = value
    .slice(0, 16)
    .map((entry, index): AnnotationTrigger | null => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        return null;
      }

      const candidate = entry as Record<string, unknown>;
      const event = normalizeDraftTriggerEvent(candidate.event);
      const eventName =
        event === 'event' && typeof candidate.eventName === 'string'
          ? preserveString(candidate.eventName)
          : undefined;
      if (event === 'event' && !eventName) {
        return null;
      }
      return {
        id: preserveString(candidate.id, `trigger_${index + 1}`),
        event,
        ...(event === 'interact' ? { action: preserveString(candidate.action) } : {}),
        ...(eventName ? { eventName } : {}),
        ...(event === 'proximity_enter' || event === 'proximity_exit'
          ? {
              radius: Math.max(1, Math.floor(toFiniteNumber(candidate.radius, 2))),
            }
          : {}),
        ...((normalizeAnnotationConditionGroup(candidate.conditions) ??
        buildLegacyConditionGroup(candidate.condition))
          ? {
              conditions:
                normalizeAnnotationConditionGroup(candidate.conditions) ??
                buildLegacyConditionGroup(candidate.condition),
            }
          : {}),
        ...(Number.isFinite(Number(candidate.cooldownMs))
          ? { cooldownMs: Math.max(0, Math.floor(Number(candidate.cooldownMs))) }
          : {}),
        effect: clampDraftTriggerEffect(event, candidate.effect),
      } as AnnotationTrigger; // REASON: event/eventName pairing validated above (see event validation block earlier in this function)
    })
    .filter((trigger): trigger is AnnotationTrigger => trigger !== null);

  return triggers.length > 0 ? triggers : undefined;
};

const clampDraftAnnotationInteractions = (
  value: unknown,
  annotation: WorldAnnotation,
  layout: WorldLayout,
): AnnotationInteraction[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const interactions = value
    .slice(0, 12)
    .map((entry, index): AnnotationInteraction | null => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        return null;
      }

      const candidate = entry as Record<string, unknown>;
      const effectType =
        candidate.effectType === 'state_change' ||
        candidate.effectType === 'emote' ||
        candidate.effectType === 'navigate'
          ? candidate.effectType
          : 'text';
      const action = preserveString(candidate.action, `action_${index + 1}`);
      const label = preserveString(candidate.label, action || `action_${index + 1}`);
      const description = preserveString(candidate.description);

      return {
        action,
        label,
        description,
        effectType,
        effectPayload:
          effectType === 'navigate'
            ? clampDraftNavigatePayload(candidate.effectPayload, layout, annotation)
            : effectType === 'emote'
              ? clampDraftEmotePayload(candidate.effectPayload)
              : preserveString(candidate.effectPayload),
        ...(normalizeAnnotationStateRecord(candidate.requiredState)
          ? { requiredState: normalizeAnnotationStateRecord(candidate.requiredState) }
          : {}),
        ...(normalizeAnnotationStateRecord(candidate.stateChange)
          ? { stateChange: normalizeAnnotationStateRecord(candidate.stateChange) }
          : {}),
      };
    })
    .filter((interaction): interaction is AnnotationInteraction => interaction !== null);

  return interactions.length > 0 ? interactions : undefined;
};

export const clampAnnotationDraftRecord = (
  annotation: WorldAnnotation,
  layout: WorldLayout,
): WorldAnnotation | null => {
  const id = sanitizeString(annotation.id);
  if (!id) {
    return null;
  }

  const shape = normalizeShape(annotation.shape);
  const position = normalizePosition(annotation.position, layout);
  const base: WorldAnnotation = {
    ...annotation,
    id,
    name: preserveString(annotation.name),
    description: preserveString(annotation.description),
    category: normalizeCategory(annotation.category),
    shape,
    position,
    ...(typeof annotation.playerVisible === 'boolean'
      ? { playerVisible: annotation.playerVisible }
      : {}),
    ...(typeof annotation.examineText === 'string' ? { examineText: annotation.examineText } : {}),
    ...(Number.isFinite(annotation.interactionRadius)
      ? {
          interactionRadius: Math.max(
            1,
            Math.floor(toFiniteNumber(annotation.interactionRadius, DEFAULT_INTERACTION_RADIUS)),
          ),
        }
      : {}),
    ...(clampDraftAnnotationInteractions(annotation.interactions, annotation, layout)
      ? {
          interactions: clampDraftAnnotationInteractions(
            annotation.interactions,
            annotation,
            layout,
          ),
        }
      : {}),
    ...(clampDraftAnnotationTriggers(annotation.triggers)
      ? { triggers: clampDraftAnnotationTriggers(annotation.triggers) }
      : {}),
    ...(normalizeAnnotationStateRecord(annotation.state)
      ? { state: normalizeAnnotationStateRecord(annotation.state) }
      : {}),
    ...(normalizeFunction(annotation.function)
      ? { function: normalizeFunction(annotation.function) }
      : {}),
    ...(normalizeCapacity(annotation.capacity)
      ? { capacity: normalizeCapacity(annotation.capacity) }
      : {}),
    ...(normalizeProductionConfig(annotation.productionConfig)
      ? { productionConfig: normalizeProductionConfig(annotation.productionConfig) }
      : {}),
    ...(normalizeResourceNodeConfig(annotation.resourceNodeConfig)
      ? { resourceNodeConfig: normalizeResourceNodeConfig(annotation.resourceNodeConfig) }
      : {}),
    ...(annotation.doorTarget
      ? { doorTarget: normalizeDoorTarget(annotation.doorTarget, layout) }
      : {}),
  };

  if (shape === 'rect') {
    const size = normalizeSize(annotation.size ?? { width: 1, height: 1 }, layout) ?? {
      width: 1,
      height: 1,
    };
    const rect = clampRectToLayout(position, size, layout);
    return {
      ...base,
      position: rect.position,
      size: rect.size,
      radius: undefined,
    };
  }

  if (shape === 'circle') {
    return {
      ...base,
      radius: Math.max(1, Math.floor(toFiniteNumber(annotation.radius, 1))),
      size: undefined,
    };
  }

  return {
    ...base,
    size: undefined,
    radius: undefined,
  };
};
